/**
 * practice-bridge.ts — host-side glue between the native PitchTracker module
 * and the WebView RPC notification channel.
 *
 * The Practice consumer runs inside the WebView; the native shell hosts the
 * real PitchTracker. This bridge turns the module's per-event `addListener`
 * stream into a single sink, which the shell forwards as the `pitchTrackerEvent`
 * notification. The `startPractice / stopPractice / pausePractice /
 * resumePractice` RPC methods drive the session.
 *
 * Transport-agnostic: callers supply both the `notify` sink (the native shell
 * wires it to `notifyWeb(...)`, tests collect into an array) and the
 * PitchTrackerModule — the shell resolves the real native module (mock only as
 * a dev-shell graceful fallback), tests pass the deterministic mock. The bridge
 * itself has no mock path.
 *
 * Invariant (GOAL.md "NEVER VIOLATE"): only Analysis Frames cross to JS —
 * `pitch_frame` carries a NativeF0Frame, never raw PCM.
 */
import type {
  PitchTrackerConfig,
  PitchTrackerEvent,
  PitchTrackerEventName,
  PitchTrackerModule,
  PitchTrackerSessionInfo,
  Subscription,
} from '../types/pitch-tracker';

/** Sink for forwarded PitchTracker events (→ `pitchTrackerEvent` notification). */
export type PitchEventSink = (event: PitchTrackerEvent) => void;

/** 브라우저 window 중 이 파일이 쓰는 부분만. DOM lib 없이 RN 번들에서도 컴파일된다. */
interface BrowserWindowLike {
  dispatchEvent(event: unknown): boolean;
}

export interface PracticeBridge {
  startPractice(config: PitchTrackerConfig): Promise<PitchTrackerSessionInfo>;
  stopPractice(): Promise<void>;
  pausePractice(): Promise<void>;
  resumePractice(): Promise<void>;
  /** Apply a runtime config patch live (no session restart) — dev tuning (#32). */
  updateConfig(config: Partial<PitchTrackerConfig>): Promise<void>;
  /** Detach all listeners (call when the WebView unmounts). */
  dispose(): void;
}

// Exhaustive flag map → adding a PitchTrackerEvent variant forces an entry here,
// so no event channel is ever silently dropped from the bridge.
const EVENT_NAME_FLAGS: Record<PitchTrackerEventName, true> = {
  session_started: true,
  pitch_frame: true,
  input_level: true,
  stream_discontinuity: true,
  performance_metrics: true,
  config_changed: true,
  interruption_started: true,
  interruption_ended: true,
  error: true,
  session_stopped: true,
};

/**
 * 모든 이벤트 채널 이름. 브리지 말고도 **모듈 하나를 다른 모듈에 통째로 이어 붙이는**
 * 쪽(브라우저 소스 스위처)이 같은 목록을 필요로 하므로 내보낸다 — 목록을 복제하면
 * 이벤트 variant가 추가될 때 한쪽만 갱신되어 채널이 조용히 사라진다.
 */
export const PITCH_EVENT_NAMES = Object.keys(
  EVENT_NAME_FLAGS,
) as PitchTrackerEventName[];

const EVENT_NAMES = PITCH_EVENT_NAMES;

export const createPracticeBridge = (
  notify: PitchEventSink,
  module: PitchTrackerModule,
): PracticeBridge => {
  const subscriptions: Subscription[] = EVENT_NAMES.map((name) =>
    module.addListener(name, notify),
  );

  return {
    startPractice: (config) => module.start(config),
    stopPractice: () => module.stop(),
    pausePractice: () => module.pause(),
    resumePractice: () => module.resume(),
    updateConfig: (config) => module.updateConfig(config),
    dispose: () => {
      for (const sub of subscriptions) sub.remove();
    },
  };
};

/**
 * 브리지 이벤트를 **네이티브 푸시 채널로도** 흘린다(브라우저 전용 parity).
 *
 * 실기기에서는 네이티브 셸이 모든 `pitchTrackerEvent`를 이 채널에 밀어 넣고, 그래서
 * 채널을 직접 구독하는 화면(디버그 콘솔의 음정 그래프·리드아웃·레벨 미터)이 값을 본다.
 * 브라우저에서는 mock 브리지가 호출자 콜백으로만 이벤트를 주므로 그 화면들이 **영원히
 * 빈 채로** 남는다 — 세션은 running인데 그래프에 선 하나 없는 상태가 그것이다.
 *
 * 셸이 하는 일을 그대로 흉내 낸다: 같은 CustomEvent, 같은 envelope 모양.
 */
export const forwardToNativeChannel = (event: PitchTrackerEvent): void => {
  // DOM 타입에 기대지 않는다 — 이 파일은 RN 번들(DOM lib 없음)에도 들어간다.
  // native-host.ts의 getBrowserWindow와 같은 판별: DOM API가 실제로 있는 window만
  // 브라우저로 본다(RN의 부분 window 전역에 속지 않게).
  const g = globalThis as { window?: BrowserWindowLike; CustomEvent?: unknown };
  const win = g.window;
  if (typeof win?.dispatchEvent !== 'function' || !g.CustomEvent) {
    return;
  }
  const Ctor = g.CustomEvent as new (
    type: string,
    init: { detail: unknown },
  ) => unknown;
  win.dispatchEvent(
    new Ctor('muzly-native-message', {
      detail: {
        kind: 'notification',
        event: 'pitchTrackerEvent',
        payload: event,
      },
    }),
  );
};
